import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Home, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { fireConversionEvents, pushLeadSubmittedEvent } from '../components/TrackingScripts';
import PhoneLink, { PHONES } from '../components/PhoneLink';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const readSession = (key) => {
  try {
    return JSON.parse(sessionStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

const readLeadMeta = () => readSession('conversion_lead');

// Personalised confirmation block (data comes from sessionStorage, never from the URL)
const PersonalGreeting = ({ meta }) => {
  const naam = meta?.naam?.trim();
  const situatie = meta?.situatie ? meta.situatie.charAt(0).toLowerCase() + meta.situatie.slice(1) : '';
  const spoed = meta?.urgentie === 'Spoed';
  let text = 'Gino of Bjorn belt u binnen 24 uur.';
  if (situatie) {
    text = spoed
      ? `Uw aanvraag voor ${situatie} is binnen. Gino of Bjorn belt u vandaag nog.`
      : `Uw aanvraag voor ${situatie} is binnen. Gino of Bjorn belt u binnen 24 uur met een vaste prijs.`;
  }
  return (
    <div className="mb-10" data-testid="bedankt-greeting">
      <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
        <CheckCircle className="w-14 h-14 text-green-500" />
      </div>
      <h1 className="text-3xl md:text-4xl font-display text-[#1C1C2E] mb-4" data-testid="bedankt-title">
        {naam ? `Ontvangen, ${naam}.` : 'Ontvangen.'}
      </h1>
      <p className="text-lg text-[#7A7A8C] leading-relaxed mb-6" data-testid="bedankt-text">{text}</p>
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-[#1C1C2E]">
        <span className="whitespace-nowrap">
          Gino <PhoneLink phone={PHONES.gino} locatie="bedankt" className="font-semibold text-[#C8963E] hover:underline" />
        </span>
        <span className="whitespace-nowrap">
          Bjorn <PhoneLink phone={PHONES.bjorn} locatie="bedankt" className="font-semibold text-[#C8963E] hover:underline" />
        </span>
      </div>
    </div>
  );
};

// Keep /bedankt out of search engines
const useNoIndex = () => {
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    const previous = meta?.getAttribute('content');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (previous) meta.setAttribute('content', previous);
    };
  }, []);
};

// Synchronously claim token + lead meta so concurrent effect runs can never fire twice
const claimConversion = (searchParams) => {
  const token = sessionStorage.getItem('conversion_token') || searchParams.get('token');
  const leadMeta = readLeadMeta();
  sessionStorage.removeItem('conversion_token');
  sessionStorage.removeItem('conversion_lead');
  return token ? { token, leadMeta } : null;
};

const ThankYouPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [leadEvent, setLeadEvent] = useState(null);
  const [greeting] = useState(() => readSession('bedankt_meta'));
  useNoIndex();

  // Push GTM event only after the page has actually rendered (loading done)
  useEffect(() => {
    if (loading || !leadEvent) return;
    pushLeadSubmittedEvent(leadEvent);
    setLeadEvent(null);
  }, [loading, leadEvent]);

  useEffect(() => {
    const claimed = claimConversion(searchParams);

    const loadPageAndTrackConversion = async () => {
      try {
        // Always load thank you page settings (page is always accessible for Google Ads)
        const settingsResponse = await axios.get(`${API}/thankyou`);
        setSettings(settingsResponse.data);
        
        // If we claimed a token, validate it and fire conversion events exactly once
        if (claimed) {
          try {
            const tokenResponse = await axios.post(`${API}/conversion/validate-token`, { token: claimed.token });
            
            if (tokenResponse.data.valid) {
              fireConversionEvents(settingsResponse.data);
              setLeadEvent({
                ...(claimed.leadMeta || {}),
                conversion_value: settingsResponse.data.tracking_conversion_value || 0
              });
            }
          } catch (tokenError) {
            console.log('Token validation failed:', tokenError);
          }
        }
        
        // Clean URL if token was in URL param
        if (searchParams.get('token')) {
          window.history.replaceState({}, '', '/bedankt');
        }
        
      } catch (error) {
        console.error('Failed to load settings:', error);
        // Still show page with default content
        setSettings({
          thankyou_title: 'Bedankt voor uw aanvraag!',
          thankyou_text: 'We hebben uw aanvraag goed ontvangen. Een van onze erkende schatters neemt binnen 24 uur contact met u op om een afspraak in te plannen.'
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadPageAndTrackConversion();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C8963E]" />
      </div>
    );
  }

  if (!settings) {
    return null; // Still loading
  }

  return (
    <div className="min-h-screen bg-[var(--cream)] flex flex-col">
      {/* Header */}
      <header className="bg-[#1C1C2E] py-6">
        <div className="container mx-auto px-4">
          <a href="/" className="inline-block">
            <span className="text-2xl font-display text-[#C8963E]">uwerkendschatter.be</span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-xl text-center">
          <PersonalGreeting meta={greeting} />

          {/* Title */}
          <h2 className="text-2xl md:text-3xl font-display text-[#1C1C2E] mb-6">
            {settings.thankyou_title}
          </h2>

          {/* Text - safely render with line breaks */}
          <div className="text-lg text-[#7A7A8C] mb-8 leading-relaxed">
            {settings.thankyou_text.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < settings.thankyou_text.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </div>

          {/* What to expect box */}
          <div className="bg-white rounded-xl border border-[#E8E4DC] p-6 mb-8 text-left">
            <h3 className="font-semibold text-[#1C1C2E] mb-4 flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-[#C8963E]" />
              Wat kunt u verwachten?
            </h3>
            <ul className="space-y-3 text-[#666]">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-[#C8963E]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#C8963E] text-sm font-semibold">1</span>
                </span>
                <span>Een van onze erkende schatters neemt binnen 24 uur contact met u op</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-[#C8963E]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#C8963E] text-sm font-semibold">2</span>
                </span>
                <span>We plannen samen een geschikt moment voor het plaatsbezoek</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-[#C8963E]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#C8963E] text-sm font-semibold">3</span>
                </span>
                <span>Na het bezoek ontvangt u binnen 5 werkdagen uw schattingsverslag</span>
              </li>
            </ul>
          </div>

          {/* Back to Home Button */}
          <a
            href="/"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#C8963E] text-white font-medium rounded-xl hover:bg-[#B8862E] transition-colors"
          >
            <Home className="w-5 h-5" />
            Terug naar homepage
          </a>

          {/* Contact info */}
          <p className="mt-8 text-sm text-[#999]">
            Vragen? Neem contact op via{' '}
            <a href="mailto:info@uwerkendschatter.be" className="text-[#C8963E] hover:underline">
              info@uwerkendschatter.be
            </a>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1C1C2E] py-6">
        <div className="container mx-auto px-4 text-center text-sm text-gray-400">
          © {new Date().getFullYear()} uwerkendschatter.be · Erkend Vlabel Schatter
        </div>
      </footer>
    </div>
  );
};

export default ThankYouPage;
